import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { apiService } from "../services/api";
import type { Country } from "../types/country";
import { isTimestampWithinWindow } from "../utils/helpers";
type CountryQueryParams = Parameters<typeof apiService.getCountries>[0];

interface CountryState {
  countries: Country[];
  loading: boolean;
  isRefreshing: boolean;
  hasLoaded: boolean;
  lastFetchedAt: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  fetchCountries: (params?: Record<string, unknown>) => Promise<void>;
  loadIfNeeded: () => Promise<void>;
  refreshCountries: () => Promise<void>;
  fetchCountryByCode: (countryCode: string) => Promise<Country>;
  addCountry: (country: {
    country_code: string;
    country_name: string;
    status?: "active" | "inactive" | "deleted";
    geom?: string | null;
  }) => Promise<void>;
  updateCountry: (
    countryCode: string,
    updates: {
      country_name?: string;
      status?: "active" | "inactive" | "deleted";
      geom?: string | null;
    },
  ) => Promise<void>;
  removeCountry: (countryCode: string) => Promise<void>;
}

export const useCountryStore = create<CountryState>()(
  devtools(
    persist(
      (set, get) => ({
        countries: [],
        loading: false,
        isRefreshing: false,
        hasLoaded: false,
        lastFetchedAt: null,
        totalCount: 0,
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 10,

        fetchCountries: async (
          params = { page: 1, limit: 20, include_all_statuses: true },
        ) => {
          set({ loading: true });
          try {
            const response = (await apiService.getCountries(
              params as CountryQueryParams,
            )) as {
              countries: Country[];
              pagination?: {
                total: number;
                page: number;
                limit: number;
                totalPages: number;
              };
            };
            const pagination = response.pagination || {
              total: response.countries.length,
              page: 1,
              limit: response.countries.length,
              totalPages: 1,
            };
            set({
              countries: response.countries,
              totalCount: pagination.total,
              currentPage: pagination.page,
              totalPages: pagination.totalPages,
              itemsPerPage: pagination.limit,
              hasLoaded: true,
              lastFetchedAt: new Date().toISOString(),
              loading: false,
            });
          } catch (error) {
            set({ loading: false });
            throw error;
          }
        },

        loadIfNeeded: async () => {
          const {
            hasLoaded,
            countries,
            isRefreshing,
            loading,
            lastFetchedAt,
            refreshCountries,
          } = get();
          if (isRefreshing || loading) {
            return;
          }
          if (
            hasLoaded &&
            countries.length > 0 &&
            isTimestampWithinWindow(lastFetchedAt, 60 * 60 * 1000, Date.now())
          ) {
            return;
          }
          await refreshCountries();
        },

        refreshCountries: async () => {
          set({ loading: true, isRefreshing: true });
          try {
            const response = (await apiService.getAllCountries({
              sort_by: "country_name",
              sort_order: "asc",
            })) as {
              countries: Country[];
            };
            set({
              countries: response.countries,
              totalCount: response.countries.length,
              currentPage: 1,
              totalPages: 1,
              itemsPerPage: response.countries.length || 1,
              hasLoaded: true,
              lastFetchedAt: new Date().toISOString(),
              loading: false,
              isRefreshing: false,
            });
          } catch (error) {
            set({ loading: false, isRefreshing: false });
            throw error;
          }
        },

        fetchCountryByCode: async (countryCode) => {
          return (await apiService.getCountry(countryCode)) as Country;
        },

        addCountry: async (country) => {
          const created = (await apiService.createCountry({
            ...country,
            status: country.status ?? undefined,
          })) as Country;
          set((state) => ({
            countries: [created, ...state.countries],
            totalCount: state.totalCount + 1,
          }));
        },

        updateCountry: async (countryCode, updates) => {
          const updated = (await apiService.updateCountry(
            countryCode,
            updates,
          )) as Country;
          set((state) => ({
            countries: state.countries.map((country) =>
              country.country_code === countryCode
                ? { ...country, ...updated }
                : country,
            ),
          }));
        },

        removeCountry: async (countryCode) => {
          await apiService.deleteCountry(countryCode);
          set((state) => ({
            countries: state.countries.map((country) =>
              country.country_code === countryCode
                ? { ...country, status: "deleted" as const }
                : country,
            ),
          }));
        },
      }),
      {
        name: "country-store",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          countries: state.countries,
          hasLoaded: state.hasLoaded,
          lastFetchedAt: state.lastFetchedAt,
          totalCount: state.totalCount,
        }),
      },
    ),
    { name: "country-store-devtools" },
  ),
);
